import fp from 'lodash/fp'
import { observable } from 'mobx'
import CertificateTypes from 'todistukset-common/src/vtj/CertificateTypes.enum'
import CertificateSubtypes from 'todistukset-common/src/vtj/CertificateSubtypes.enum'
import { CertificateFeature } from 'todistukset-common/src/vtj/CertificateFeature.enum'
import { getEnabledFeatures } from 'todistukset-common/src/vtj/certificate.util'
import { EuCountries } from 'todistukset-common/src/vtj/EuCountries.enum'
import {
  getOrdererFromUser,
  isEidasIdentifiedUser,
} from 'todistukset-common/src/vtj/user.utils'
import type { TodistuksetUser } from 'todistukset-common/src/vtj/types/user'
import {
  getDefaultPersons,
  populateAndSanitizeMailDelivery,
} from 'todistukset-ui/src/vtj/stores/util/form.util'
import { TodistusStoreForm } from 'todistukset-common/src/vtj/types/form-types.type'
import { checkIfMailDeliveryIsEnabled } from 'todistukset-common/src/vtj/form-check.util'
import {
  EditableField,
  editIlmoitettuArvo,
  IncludeApostille,
  IncludeHetu,
  IncludeKaannos,
  IncludeMaritalStatus,
  IncludePreviousPermanentAddresses,
  IncludePreviousNames,
  IncludeSpousePreviousNames,
  PreviousAddressesOverYears,
  Puhelinnumero,
  IncludePreviousMarriages,
  KaannosKieli,
  IncludeNationality,
  IncludePermanentAddress,
  IncludePreviousTemporaryAddresses,
  IncludePreviousKotikunta,
  IncludeKotikunta,
  IncludeParentsHetu,
  IncludeParentsPreviousNames,
  IncludeNumberOfMarriages,
} from 'todistukset-common/src/vtj/types/editable-field.type'
import { TodistusAsiointikieli } from 'todistukset-common/src/vtj/types/todistus-person.type'

export const createFormStore = ({
  user,
  todistusType,
  asiointikieli,
}: {
  user: TodistuksetUser
  todistusType: CertificateTypes
  asiointikieli: TodistusAsiointikieli
}): TodistusStoreForm => {
  const defaultUsageRegion = {
    target: null,
    countryKoodistoCode: EuCountries.FINLAND,
    receiver: null,
  }

  const ordererAndSubject: Pick<
    TodistusStoreForm,
    'orderer' | 'persons' | 'ordererExtraIdentification'
  > = isEidasIdentifiedUser(user)
    ? {
        orderer: getOrdererFromUser(user),
        ordererExtraIdentification: {
          birthPlace: '',
          birthCountry: '',
          finnishHetu: null,
          foreignPersonId: null,
          foreignPersonIdCountry: null,
          previousName: null,
        },
        persons: getDefaultPersons({ user, todistusType, asiointikieli }),
      }
    : {
        orderer: getOrdererFromUser(user),
        persons: getDefaultPersons({
          user,
          todistusType,
          asiointikieli,
        }),
      }

  const todistusSubtype = checkIfMailDeliveryIsEnabled({
    usageRegion: defaultUsageRegion,
    orderer: ordererAndSubject.orderer,
  })
    ? CertificateSubtypes.EU_VIRANOMAISELLE
    : CertificateSubtypes.SAHKOINEN

  const mailDelivery = populateAndSanitizeMailDelivery({
    user,
    customValues: user.streetAddress
      ? {
          country: editIlmoitettuArvo<EditableField<string>>(null, 'Suomi'),
        }
      : {},
  })

  const includeKotikunta = getEnabledFeatures({
    certificateType: todistusType,
    isAdult: user.isAdult,
  }).includes(CertificateFeature.INCLUDE_KOTIKUNTA)

  const includeParentsPreviousNames = getEnabledFeatures({
    certificateType: todistusType,
    isAdult: user.isAdult,
  }).includes(CertificateFeature.INCLUDE_PARENTS_PREVIOUS_NAMES)

  const includePermanentAddress = getEnabledFeatures({
    certificateType: todistusType,
    isAdult: user.isAdult,
  }).includes(CertificateFeature.INCLUDE_PERMANENT_ADDRESS)

  const includeNationality = todistusType === CertificateTypes.SYNTYMATODISTUS
  const includePreviousNames = todistusType === CertificateTypes.SYNTYMATODISTUS

  return observable({
    todistusType,
    todistusSubtype,
    todistusLanguage: null,
    translationFormLanguage: editIlmoitettuArvo<KaannosKieli>(null, null),
    usageRegion: defaultUsageRegion,
    includeTranslationForm: editIlmoitettuArvo<IncludeKaannos>(null, false),
    includeApostille: editIlmoitettuArvo<IncludeApostille>(null, false),
    mailDelivery,
    contactInfo: {
      email: editIlmoitettuArvo<Puhelinnumero>(null, ''),
      phoneNumber: editIlmoitettuArvo<Puhelinnumero>(null, ''),
    },
    ...ordererAndSubject,
    ordererExtraIdentificationOptions: {
      addForeignPersonId: false,
      addPreviousName: false,
    },
    includePreviousNames: editIlmoitettuArvo<IncludePreviousNames>(
      null,
      includePreviousNames
    ),
    includeSpousePreviousNames: editIlmoitettuArvo<IncludeSpousePreviousNames>(
      null,
      false
    ),
    includePreviousMarriages: editIlmoitettuArvo<IncludePreviousMarriages>(
      null,
      false
    ),
    includeHetu: editIlmoitettuArvo<IncludeHetu>(
      null,
      checkIfHetuIsTrue({ user, todistusType })
    ),
    includeMaritalStatus: editIlmoitettuArvo<IncludeMaritalStatus>(null, false),
    includePreviousPermanentAddresses:
      editIlmoitettuArvo<IncludePreviousPermanentAddresses>(null, false),
    previousAddressesOverYears: editIlmoitettuArvo<PreviousAddressesOverYears>(
      null,
      1
    ),
    includeNationality: editIlmoitettuArvo<IncludeNationality>(
      null,
      includeNationality
    ),
    includePermanentAddress: editIlmoitettuArvo<IncludePermanentAddress>(
      null,
      includePermanentAddress
    ),
    includePreviousTemporaryAddresses:
      editIlmoitettuArvo<IncludePreviousTemporaryAddresses>(null, false),
    includePreviousKotikunta: editIlmoitettuArvo<IncludePreviousKotikunta>(
      null,
      false
    ),
    includeKotikunta: editIlmoitettuArvo<IncludeKotikunta>(
      null,
      includeKotikunta
    ),
    includeParentsHetu: editIlmoitettuArvo<IncludeParentsHetu>(null, false),
    includeParentsPreviousNames:
      editIlmoitettuArvo<IncludeParentsPreviousNames>(
        null,
        includeParentsPreviousNames
      ),
    includeNumberOfMarriages: editIlmoitettuArvo<IncludeNumberOfMarriages>(
      null,
      false
    ),
  })
}

const checkIfHetuIsTrue = ({
  user,
  todistusType,
}: {
  user: { isAdult: boolean }
  todistusType: CertificateTypes
}) => {
  const feature = CertificateFeature.INCLUDE_HETU
  const enabledFeatures = getEnabledFeatures({
    isAdult: user.isAdult,
    certificateType: todistusType,
  })
  return fp.includes(feature, enabledFeatures)
}
